// src/components/resources/glossary/glossary-shell.ts

/**
 * @fileoverview Shared shell geometry/background for the glossary term page.
 *
 * The glossary term route renders TWO shells around the same fixed breadcrumb
 * bar: the server-rendered HydrationGate fallback (`GlossaryTermServerContent`
 * — the page's first-pass HTML for crawlers/no-JS) and the post-hydration
 * client layout (`GlossaryLayout` with `showNavigation={false}`, mounted by
 * `GlossaryTermLayout`). HydrationGate REPLACES the fallback subtree after
 * mount, so any divergence between the two shells becomes a visible jump —
 * content shifting and/or the background changing — at the swap.
 *
 * These constants are that alignment, by construction: both shells import the
 * same class strings, so the fixed-bar geometry, content offset, and
 * background cannot drift apart again (review threads `r3540554059` and the
 * fallback-overlap report on #534 both trace back to hand-copied values).
 */

/**
 * Root wrapper of the shell. Transparent: the route page
 * (`resources/glossary/[category]/[slug]/page.tsx`) owns the actual
 * background (`bg-gray-50 dark:bg-gray-950`), so both pre- and post-hydration
 * trees show the identical backdrop.
 */
export const GLOSSARY_SHELL_ROOT_CLASSNAME = 'min-h-screen bg-transparent'

/**
 * Backdrop gradient behind the fixed navbar + breadcrumb bar in its
 * unscrolled state (the only state the server fallback can render).
 */
export const GLOSSARY_SHELL_BAR_BACKDROP_CLASSNAME =
  'bg-gradient-to-b from-white/60 via-white/30 to-transparent dark:from-gray-950/60 dark:via-gray-950/30 dark:to-transparent'

/** Height (px) of the fixed-bar backdrop gradient when no category nav shows. */
export const GLOSSARY_SHELL_BAR_BACKDROP_HEIGHT_PX = 136

/** Spacer reserving room for the site navbar inside the fixed bar (h-20 = 80px). */
export const GLOSSARY_SHELL_NAVBAR_SPACER_CLASSNAME = 'h-[80px]'

/**
 * Top offset applied to the page content so it starts below the navbar
 * (`showNavigation={false}` variant — the glossary term page).
 */
export const GLOSSARY_SHELL_CONTENT_OFFSET_CLASSNAME = 'pt-[80px]'

/**
 * The content container both shells place directly inside the offset wrapper.
 * `py-12` + the {@link GLOSSARY_SHELL_CONTENT_TOP_GAP_CLASSNAME} below put the
 * first rendered content at 80 + 48 + 24 = 152px — clear of the fixed bar
 * (80px navbar + ~60px trail ≈ 140px) — identically pre- and post-hydration.
 */
export const GLOSSARY_SHELL_CONTENT_CONTAINER_CLASSNAME =
  'mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12'

/**
 * Gap between the container top and the first content block. The enhanced
 * layout carries this on its content grid (`pt-6`); the server fallback
 * mirrors it on the article wrapper so the swap doesn't move the first line.
 */
export const GLOSSARY_SHELL_CONTENT_TOP_GAP_CLASSNAME = 'pt-6'
